import { useEffect, useState } from "react";
import {
  getGa4AnalyticsAdminCredentialsStatus,
  saveGa4AnalyticsAdminCredentials,
} from "../../../../api/ga4AnalyticsSourceControls";
import AdminBtn from "../layout/AdminBtn";
import SourceHealthBadge from "../SourceHealthBadge";

function formatCredentialsSource(source) {
  if (source === "env_json") return "Environment variable (inline JSON)";
  if (source === "env_path") return "Environment variable (file path)";
  if (source === "persisted_file") return "Saved on server";
  return "Not configured";
}

function parseServiceAccountJson(rawValue) {
  const trimmed = String(rawValue || "").trim();
  if (!trimmed) {
    return { ok: false, message: "Paste the service account JSON key file contents." };
  }

  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch (error) {
    return { ok: false, message: `Invalid JSON: ${error.message}` };
  }
}

export default function Ga4AnalyticsSourceConfigureModal({
  source,
  isOpen,
  isSaving,
  onClose,
  onSave,
  onCredentialsSaved,
}) {
  const [enabled, setEnabled] = useState(true);
  const [measurementId, setMeasurementId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [pollIntervalMinutes, setPollIntervalMinutes] = useState("60");
  const [syncLookbackDays, setSyncLookbackDays] = useState("7");
  const [credentialsStatus, setCredentialsStatus] = useState(null);
  const [credentialsJson, setCredentialsJson] = useState("");
  const [credentialsError, setCredentialsError] = useState("");
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);

  useEffect(() => {
    if (!source || !isOpen) {
      return;
    }

    setEnabled(source.enabled !== false);
    setMeasurementId(source.storeConfig?.measurementId || source.measurementId || "");
    setPropertyId(source.storeConfig?.propertyId || source.propertyId || "");
    setPollIntervalMinutes(
      String(source.storeConfig?.pollIntervalMinutes || source.pollIntervalMinutes || 60)
    );
    setSyncLookbackDays(
      String(source.storeConfig?.syncLookbackDays || source.syncLookbackDays || 7)
    );
    setCredentialsJson("");
    setCredentialsError("");
  }, [isOpen, source]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;
    setIsLoadingCredentials(true);

    getGa4AnalyticsAdminCredentialsStatus()
      .then((body) => {
        if (!cancelled) {
          setCredentialsStatus(body.status || null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setCredentialsError(error.message || "Unable to load credentials status.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingCredentials(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen || !source) {
    return null;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave(source, {
      enabled,
      measurementId: measurementId.trim(),
      propertyId: propertyId.trim(),
      pollIntervalMinutes: Number(pollIntervalMinutes),
      syncLookbackDays: Number(syncLookbackDays),
    });
  };

  const handleCredentialsFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setCredentialsJson(text);
      setCredentialsError("");
    } catch (error) {
      setCredentialsError(error.message || "Unable to read the selected file.");
    } finally {
      event.target.value = "";
    }
  };

  const handleSaveCredentials = async () => {
    const parsed = parseServiceAccountJson(credentialsJson);
    if (!parsed.ok) {
      setCredentialsError(parsed.message);
      return;
    }

    setIsSavingCredentials(true);
    setCredentialsError("");
    try {
      const body = await saveGa4AnalyticsAdminCredentials(parsed.value);
      setCredentialsStatus(body.status || null);
      setCredentialsJson("");
      await onCredentialsSaved?.(body.status);
    } catch (error) {
      setCredentialsError(error.message || "Failed to save GA4 credentials.");
    } finally {
      setIsSavingCredentials(false);
    }
  };

  const credentialsConfigured =
    credentialsStatus?.configured || source.credentialsPresent || false;

  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-re-modal panel-card admin-re-modal--wide admin-ga4-config-modal"
        role="dialog"
        aria-labelledby="ga4-config-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Google Analytics 4</p>
            <h2 id="ga4-config-modal-title">Configure {source.sourceName}</h2>
            <p className="muted admin-re-source-code">{source.sourceKey}</p>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>

        <form className="admin-re-modal-body" onSubmit={handleSubmit}>
          <section className="admin-ga4-config-section">
            <div className="admin-ga4-config-section__header">
              <h3>Server credentials</h3>
              <SourceHealthBadge status={credentialsConfigured ? "healthy" : "warning"} />
            </div>
            <p className="muted">
              Upload the Google Cloud service account JSON key with Analytics Viewer access on the
              GA4 property. This is shared across all Analytics sources on this server.
            </p>

            {isLoadingCredentials ? (
              <p className="muted">Loading credentials status…</p>
            ) : (
              <div className="admin-ga4-config-credentials-status">
                <div>
                  <span className="admin-ga4-config-label">Status</span>
                  <strong>{credentialsConfigured ? "Configured" : "Missing"}</strong>
                </div>
                {credentialsStatus?.clientEmail ? (
                  <div>
                    <span className="admin-ga4-config-label">Service account</span>
                    <code>{credentialsStatus.clientEmail}</code>
                  </div>
                ) : null}
                {credentialsStatus?.projectId ? (
                  <div>
                    <span className="admin-ga4-config-label">Project</span>
                    <code>{credentialsStatus.projectId}</code>
                  </div>
                ) : null}
                {credentialsStatus?.source ? (
                  <div>
                    <span className="admin-ga4-config-label">Source</span>
                    <span>{formatCredentialsSource(credentialsStatus.source)}</span>
                  </div>
                ) : null}
              </div>
            )}

            {credentialsStatus?.canPersistToDisk === false ? (
              <p className="muted admin-re-source-code">
                Credentials are managed through {credentialsStatus.envKeys?.json}. Update deployment
                secrets to change them.
              </p>
            ) : (
              <>
                <label className="admin-re-modal-field">
                  <span>Service account JSON</span>
                  <textarea
                    className="admin-ga4-config-json"
                    rows={8}
                    value={credentialsJson}
                    onChange={(event) => {
                      setCredentialsJson(event.target.value);
                      setCredentialsError("");
                    }}
                    placeholder='{"type":"service_account","project_id":"...","private_key":"...","client_email":"..."}'
                  />
                </label>

                <label className="admin-re-modal-field">
                  <span>Or upload JSON key file</span>
                  <input type="file" accept=".json,application/json" onChange={handleCredentialsFile} />
                </label>

                {credentialsError ? (
                  <p className="admin-ga4-config-error" role="alert">
                    {credentialsError}
                  </p>
                ) : null}

                <div className="admin-ga4-config-inline-actions">
                  <AdminBtn
                    type="button"
                    variant="secondary"
                    disabled={isSavingCredentials || !credentialsJson.trim()}
                    onClick={handleSaveCredentials}
                  >
                    {isSavingCredentials ? "Saving credentials…" : "Save credentials"}
                  </AdminBtn>
                </div>
              </>
            )}
          </section>

          <section className="admin-ga4-config-section">
            <div className="admin-ga4-config-section__header">
              <h3>Source settings</h3>
              <SourceHealthBadge status={source.health || source.healthStatus || "never_run"} />
            </div>

            {!source.canConfigure ? (
              <p className="muted">
                This source does not have a Client View record yet. Create the Client View before
                saving GA4 settings.
              </p>
            ) : null}

            <div className="admin-ga4-config-grid">
              <label className="admin-re-modal-field admin-re-modal-toggle admin-ga4-config-grid__full">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                  disabled={!source.canConfigure}
                />
                <span>GA4 polling enabled</span>
              </label>

              <label className="admin-re-modal-field">
                <span>Measurement ID</span>
                <input
                  type="text"
                  value={measurementId}
                  onChange={(event) => setMeasurementId(event.target.value)}
                  placeholder="G-XXXXXXXXXX"
                  disabled={!source.canConfigure}
                />
              </label>

              <label className="admin-re-modal-field">
                <span>GA4 Property ID</span>
                <input
                  type="text"
                  value={propertyId}
                  onChange={(event) => setPropertyId(event.target.value)}
                  placeholder="123456789"
                  disabled={!source.canConfigure}
                />
              </label>

              <label className="admin-re-modal-field">
                <span>Poll interval (minutes)</span>
                <input
                  type="number"
                  min="5"
                  step="5"
                  value={pollIntervalMinutes}
                  onChange={(event) => setPollIntervalMinutes(event.target.value)}
                  disabled={!source.canConfigure}
                />
              </label>

              <label className="admin-re-modal-field">
                <span>Sync lookback (days)</span>
                <input
                  type="number"
                  min="1"
                  max="366"
                  value={syncLookbackDays}
                  onChange={(event) => setSyncLookbackDays(event.target.value)}
                  disabled={!source.canConfigure}
                />
              </label>
            </div>

            {source.propertyIdSource && source.propertyIdSource !== "database" ? (
              <p className="muted admin-re-source-code">
                Property ID fallback: {source.propertyIdSource}
              </p>
            ) : null}
            {source.measurementIdSource && source.measurementIdSource !== "database" ? (
              <p className="muted admin-re-source-code">
                Measurement ID fallback: {source.measurementIdSource}
              </p>
            ) : null}
          </section>

          <footer className="admin-re-modal-footer">
            <AdminBtn type="button" variant="secondary" onClick={onClose}>
              Cancel
            </AdminBtn>
            <AdminBtn type="submit" variant="primary" disabled={isSaving || !source.canConfigure}>
              {isSaving ? "Saving…" : "Save source settings"}
            </AdminBtn>
          </footer>
        </form>
      </div>
    </div>
  );
}
